import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtAestDate, fmtAestDateTime, fmtAud, todayAestISO } from "../lib/format";
import { useAuth } from "../lib/auth";
import CompleteTaskModal from "../components/CompleteTaskModal";
import TaskEditModal from "../components/TaskEditModal";
import { taskTypePillClass } from "../lib/taskTypes";
import { FileText, ListChecks, AlertTriangle, DollarSign, Clock, Camera } from "lucide-react";
import BusinessCardScanner from "../components/BusinessCardScanner";

function Tile({ icon: Icon, label, value, sub, accent, testid, to }) {
  const body = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: accent || "var(--theme-accent-50)" }}>
          <Icon size={18} />
        </div>
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider leading-tight line-clamp-2 min-w-0">
          {label}
        </div>
      </div>
      <div className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight truncate">{value}</div>
      {sub != null && <div className="text-xs text-gray-500 mt-1 truncate">{sub}</div>}
    </>
  );
  const cls = "cesco-card p-4 sm:p-5 block";
  return to
    ? <Link to={to} className={`${cls} hover:shadow-md transition-shadow`} data-testid={testid}>{body}</Link>
    : <div className={cls} data-testid={testid}>{body}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [completing, setCompleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const reloadAll = useCallback(async () => {
    const [d, t, u] = await Promise.all([
      api.get("/dashboard"),
      api.get("/tasks?status=open"),
      api.get("/users"),
    ]);
    setData(d.data); setTasks(t.data); setUsers(u.data);
  }, []);
  useEffect(() => { reloadAll(); }, [reloadAll]);

  function openComplete(task) { setCompleting(task); }

  if (!data) return <div className="skeleton h-40 w-full" data-testid="dashboard-loading"/>;

  const today = todayAestISO();
  // Sort: overdue first, then today, then upcoming (by soonest due)
  const sorted = [...tasks].sort((a, b) => {
    const av = a.due_date || "9999-12-31";
    const bv = b.due_date || "9999-12-31";
    return av.localeCompare(bv);
  });
  const shown = sorted.slice(0, 8);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Welcome, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-gray-500 text-sm">{data.is_admin ? "Admin view - all reps visible." : "Here's your day at a glance."}</p>
        </div>
        <button className="cesco-btn-primary flex items-center gap-2 shrink-0"
                onClick={()=>setScanOpen(true)}
                data-testid="btn-scan-card">
          <Camera size={16}/> Scan business card
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <Tile icon={FileText}      label="Open Quotes"     value={data.open_quotes_count}
              to="/quotes?status=OPEN" testid="tile-open-quotes"/>
        <Tile icon={DollarSign}    label={data.is_admin ? "Open Pipeline Value" : "My Open Pipeline"} value={fmtAud(data.open_pipeline_value)}
              to="/pipeline" testid="tile-pipeline-value"/>
        <Tile icon={Clock}         label="At-Risk Quotes"  value={data.at_risk_count}
              sub={fmtAud(data.at_risk_value)}
              accent="#FFF1E0" to="/quotes?at_risk=true" testid="tile-at-risk"/>
        <Tile icon={ListChecks}    label="Tasks Due Today" value={data.tasks_due_today}
              to="/tasks?due=today" testid="tile-tasks-today"/>
        <Tile icon={AlertTriangle} label="Overdue Tasks"   value={data.tasks_overdue}
              accent="#FEE6E6" to="/tasks?due=overdue" testid="tile-tasks-overdue"/>
      </div>

      {/* Open tasks feed */}
      <div className="cesco-card p-5" data-testid="open-tasks-feed">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-semibold">My open tasks</h2>
            <p className="text-xs text-gray-500">Tick to mark complete. {sorted.length} open.</p>
          </div>
          <Link to="/tasks" className="text-sm text-gray-600 hover:text-black">View all →</Link>
        </div>
        {shown.length === 0 && (
          <div className="text-sm text-gray-500 py-4 text-center">Inbox zero 🎉</div>
        )}
        <div className="space-y-2">
          {shown.map(t => {
            const overdue = t.due_date < today;
            const dueToday = t.due_date === today;
            const cls = overdue ? "feed-item-task-open" : "feed-item-note";
            return (
              <div key={t.id} className={`${cls} p-3 rounded-md flex items-start gap-3`}
                   data-testid="open-task-row">
                <input type="checkbox" className="mt-1" onChange={() => openComplete(t)}
                       data-testid="open-task-checkbox"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.summary}</div>
                  <div className="text-xs text-gray-600 mt-1 flex flex-wrap items-center gap-2">
                    {t.task_type && <span className={`pill ${taskTypePillClass(t.task_type)}`} data-testid="task-type-pill">{t.task_type}</span>}
                    <Link to={`/companies/${t.company_id}`} className="hover:underline">{t.company_name}</Link>
                    {t.contact_name && <span className="pill pill-blue">👤 {t.contact_name}</span>}
                    <span>· Due {fmtAestDate(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""}</span>
                    {overdue && <span className="pill pill-red">OVERDUE</span>}
                    {dueToday && <span className="pill pill-blue">TODAY</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button className="cesco-btn-ghost text-xs" onClick={()=>setEditing(t)}
                          data-testid="btn-edit-task">Edit</button>
                  <button className="cesco-btn-ghost text-xs" onClick={()=>openComplete(t)}
                          data-testid="btn-complete-task">Complete</button>
                </div>
              </div>
            );
          })}
        </div>
        {sorted.length > shown.length && (
          <div className="mt-3 text-center">
            <Link to="/tasks" className="text-sm text-gray-600 hover:text-black">
              + {sorted.length - shown.length} more →
            </Link>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="cesco-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Recent activity</h2>
          <Link to="/tasks" className="text-sm text-gray-600 hover:text-black">View tasks →</Link>
        </div>        {data.recent_activity.length === 0 && <div className="text-sm text-gray-500">Nothing yet - get out there.</div>}
        <div className="space-y-2">
          {data.recent_activity.map(a => (
            <div key={a.id} className="py-2 border-b last:border-none flex items-center justify-between gap-3"
                 data-testid="recent-activity-row">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {a.type === "task" ? `📋 ${a.summary}`
                    : a.type === "note" ? `📝 ${a.text?.slice(0, 80)}`
                    : `⚙ ${a.message}`}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {a.company_name} · {a.created_by_name} · {fmtAestDateTime(a.created_at)}
                </div>
              </div>
              {a.company_id && (
                <Link to={`/companies/${a.company_id}`} className="text-sm text-gray-600 hover:text-black shrink-0">Open →</Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {completing && (
        <CompleteTaskModal task={completing} users={users}
          onClose={()=>setCompleting(null)} onDone={reloadAll}/>
      )}
      {editing && (
        <TaskEditModal task={editing} users={users}
          onClose={()=>setEditing(null)} onSaved={reloadAll}/>
      )}
      {scanOpen && (
        <BusinessCardScanner
          onClose={()=>setScanOpen(false)}
          onCreated={reloadAll}/>
      )}
    </div>
  );
}
